// src/components/Chat/GroupInfoDialog.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    FormControlLabel,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import RemoveModeratorIcon from '@mui/icons-material/RemoveModerator';
import SearchIcon from '@mui/icons-material/Search';
import { apiErrorMessage } from '../../api/errors';
import {
    addGroupMembers,
    deleteGroup,
    fetchDirectoryUsers,
    fetchGroup,
    leaveGroup,
    removeGroupMember,
    renameGroup,
    setGroupMemberRole,
} from '../../api/groups';
import type { DirectoryUser, GroupDetails, GroupMember, GroupRole } from '../../types';
import {
    formatMemberCount,
    getMemberActions,
    isGroupAdmin,
    type MemberActionId,
} from './groupPermissions';

interface GroupInfoDialogProps {
    open: boolean;
    onClose: () => void;
    /** id беседы-группы; пока null, диалог показывает только заголовок. */
    groupId: string | null;
    /** id текущего пользователя — по нему находим свою строку в составе. */
    currentUserId?: string | null;
    /** Заголовок из беседы: виден, пока состав ещё грузится. */
    fallbackTitle?: string;
    /** Диалог изменил группу — шапка чата обновляет название и счётчик. */
    onGroupUpdated?: (group: GroupDetails) => void;
    /** Пользователь вышел из группы или удалил её — чат нужно закрыть. */
    onGroupRemoved?: (groupDeleted: boolean) => void;
}

/** Инициалы для аватара: «Иван Петров» → «ИП». */
const getInitials = (fullName: string): string =>
    fullName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('') || 'У';

/** Дата создания группы в коротком виде; пустую строку не показываем. */
const formatCreatedAt = (createdAt: string): string | null => {
    if (createdAt === '') return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** Подписи и иконки пунктов меню участника. */
const MEMBER_ACTIONS: Record<MemberActionId, {
    label: string;
    icon: React.ReactElement;
    danger?: boolean;
}> = {
    promote: { label: 'Сделать админом', icon: <AdminPanelSettingsIcon fontSize="small" /> },
    demote: { label: 'Снять админа', icon: <RemoveModeratorIcon fontSize="small" /> },
    remove: { label: 'Удалить из группы', icon: <PersonRemoveIcon fontSize="small" />, danger: true },
    leave: { label: 'Выйти из группы', icon: <LogoutIcon fontSize="small" />, danger: true },
};

/**
 * Карточка групповой беседы: состав, роли и управление группой.
 *
 * Права рисуются по роли из ответа бэкенда (`currentUserRole`): админ
 * переименовывает группу, добавляет и удаляет участников, выдаёт админку и
 * удаляет группу; рядовой участник видит состав и выходит из группы.
 * Последнего админа интерфейс не даёт снять или удалить — иначе группа
 * останется без управления.
 */
export const GroupInfoDialog: React.FC<GroupInfoDialogProps> = ({
    open,
    onClose,
    groupId,
    currentUserId = null,
    fallbackTitle,
    onGroupUpdated,
    onGroupRemoved,
}) => {
    const [group, setGroup] = useState<GroupDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');

    const [addingMembers, setAddingMembers] = useState(false);
    const [directory, setDirectory] = useState<DirectoryUser[]>([]);
    const [directoryLoading, setDirectoryLoading] = useState(false);
    const [memberQuery, setMemberQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuUserId, setMenuUserId] = useState<string | null>(null);

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);

    const loadGroup = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            const details = await fetchGroup(id);
            if (details) {
                setGroup(details);
                setTitleDraft(details.title);
            } else {
                setError('Не удалось прочитать данные группы');
            }
        } catch (err) {
            setError(apiErrorMessage(err, 'Не удалось загрузить группу'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDirectory = useCallback(async () => {
        setDirectoryLoading(true);
        try {
            setDirectory(await fetchDirectoryUsers());
        } catch (err) {
            setError(apiErrorMessage(err, 'Не удалось загрузить список пользователей'));
        } finally {
            setDirectoryLoading(false);
        }
    }, []);

    // Каждое открытие начинает с чистого листа: старые данные чужой группы
    // не должны мелькать в карточке.
    useEffect(() => {
        if (!open || !groupId) return;

        setGroup(null);
        setError(null);
        setEditingTitle(false);
        setAddingMembers(false);
        setDirectory([]);
        setMemberQuery('');
        setSelectedIds([]);
        setMenuAnchor(null);
        setMenuUserId(null);
        setConfirmDelete(false);
        setConfirmLeave(false);

        void loadGroup(groupId);
    }, [open, groupId, loadGroup]);

    // Справочник пользователей грузим один раз за открытие панели добавления.
    useEffect(() => {
        if (!open || !addingMembers || directory.length > 0 || directoryLoading) return;

        void loadDirectory();
    }, [open, addingMembers, directory.length, directoryLoading, loadDirectory]);

    /** Применяет ответ мутации; пустой ответ означает «перечитай группу». */
    const applyGroup = useCallback(
        (next: GroupDetails | null) => {
            if (next) {
                setGroup(next);
                setTitleDraft(next.title);
                onGroupUpdated?.(next);
                return;
            }
            if (groupId) void loadGroup(groupId);
        },
        [groupId, loadGroup, onGroupUpdated],
    );

    /** Общая обёртка мутации: индикатор, текст ошибки, разблокировка. */
    const runMutation = async (action: () => Promise<void>, fallback: string): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            await action();
        } catch (err) {
            setError(apiErrorMessage(err, fallback));
        } finally {
            setBusy(false);
        }
    };

    const role: GroupRole = group?.currentUserRole ?? 'member';
    const isAdmin = isGroupAdmin(role);
    // Ссылка на массив должна быть стабильной: от неё зависят useMemo ниже.
    const members = useMemo<GroupMember[]>(
        () => group?.members ?? [],
        [group],
    );
    const adminCount = members.filter((member) => isGroupAdmin(member.role)).length;

    const existingIds = useMemo(
        () => new Set(members.map((member) => member.userId)),
        [members],
    );

    const candidates = useMemo(() => {
        const query = memberQuery.trim().toLowerCase();

        return directory
            .filter((user) => !existingIds.has(user.id))
            .filter(
                (user) =>
                    query === ''
                    || user.fullName.toLowerCase().includes(query)
                    || user.email.toLowerCase().includes(query),
            );
    }, [directory, existingIds, memberQuery]);

    const menuMember: GroupMember | null =
        members.find((member) => member.userId === menuUserId) ?? null;
    const menuActions: MemberActionId[] = menuMember
        ? getMemberActions(menuMember, {
            currentUserId,
            currentUserRole: role,
            adminCount,
        })
        : [];

    const handleSaveTitle = async (): Promise<void> => {
        const title = titleDraft.trim();
        if (!groupId) return;
        if (title === '') {
            setError('Название группы не может быть пустым');
            return;
        }

        await runMutation(async () => {
            applyGroup(await renameGroup(groupId, title));
            setEditingTitle(false);
        }, 'Не удалось переименовать группу');
    };

    const handleToggleMember = (userId: string): void => {
        setSelectedIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId],
        );
    };

    const handleAddMembers = async (): Promise<void> => {
        if (!groupId || selectedIds.length === 0) return;

        await runMutation(async () => {
            applyGroup(await addGroupMembers(groupId, selectedIds));
            setAddingMembers(false);
            setMemberQuery('');
            setSelectedIds([]);
        }, 'Не удалось добавить участников');
    };

    const handleSetRole = async (userId: string, nextRole: GroupRole): Promise<void> => {
        if (!groupId) return;

        await runMutation(async () => {
            applyGroup(await setGroupMemberRole(groupId, userId, nextRole));
        }, 'Не удалось изменить роль участника');
    };

    const handleRemoveMember = async (userId: string): Promise<void> => {
        if (!groupId) return;

        await runMutation(async () => {
            applyGroup(await removeGroupMember(groupId, userId));
        }, 'Не удалось удалить участника');
    };

    const handleLeave = async (): Promise<void> => {
        if (!groupId) return;

        await runMutation(async () => {
            const result = await leaveGroup(groupId);
            setConfirmLeave(false);
            onGroupRemoved?.(result.groupDeleted);
            onClose();
        }, 'Не удалось выйти из группы');
    };

    const handleDelete = async (): Promise<void> => {
        if (!groupId) return;

        await runMutation(async () => {
            await deleteGroup(groupId);
            setConfirmDelete(false);
            onGroupRemoved?.(true);
            onClose();
        }, 'Не удалось удалить группу');
    };

    const handleMemberAction = async (action: MemberActionId): Promise<void> => {
        const member = menuMember;
        closeMenu();
        if (!member) return;

        if (action === 'leave') {
            setConfirmLeave(true);
            return;
        }
        if (action === 'promote') {
            await handleSetRole(member.userId, 'admin');
            return;
        }
        if (action === 'demote') {
            await handleSetRole(member.userId, 'member');
            return;
        }
        await handleRemoveMember(member.userId);
    };

    function closeMenu(): void {
        setMenuAnchor(null);
        setMenuUserId(null);
    }

    const dialogTitle = group?.title ?? fallbackTitle ?? 'Группа';
    const createdAt = group ? formatCreatedAt(group.createdAt) : null;

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            TransitionComponent={Fade}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    bgcolor: '#f4f7f6',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ color: '#9c27b0' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                        {dialogTitle}
                    </Typography>
                    <IconButton onClick={onClose} size="small" aria-label="Закрыть карточку группы">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 2, py: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading && !group && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {group && (
                    <>
                        {/* Название и краткая сводка */}
                        <Box
                            sx={{
                                bgcolor: 'white',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 1.5,
                                mb: 1.5,
                            }}
                        >
                            {editingTitle ? (
                                <Stack spacing={1}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        autoFocus
                                        label="Название группы"
                                        value={titleDraft}
                                        onChange={(event) => setTitleDraft(event.target.value)}
                                        disabled={busy}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') void handleSaveTitle();
                                            if (event.key === 'Escape') {
                                                setEditingTitle(false);
                                                setTitleDraft(group.title);
                                            }
                                        }}
                                    />
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={busy}
                                            onClick={() => void handleSaveTitle()}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                bgcolor: '#9c27b0',
                                                '&:hover': { bgcolor: '#7b1fa2' },
                                                boxShadow: 'none',
                                                borderRadius: 2,
                                            }}
                                        >
                                            Сохранить
                                        </Button>
                                        <Button
                                            size="small"
                                            disabled={busy}
                                            onClick={() => {
                                                setEditingTitle(false);
                                                setTitleDraft(group.title);
                                            }}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Отмена
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                                        {group.title}
                                    </Typography>
                                    {isAdmin && (
                                        <Tooltip title="Переименовать группу">
                                            <IconButton
                                                size="small"
                                                aria-label="Переименовать группу"
                                                disabled={busy}
                                                onClick={() => setEditingTitle(true)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                {formatMemberCount(group.memberCount)}
                                {createdAt ? ` · создана ${createdAt}` : ''}
                            </Typography>
                        </Box>

                        {/* Заголовок состава и кнопка добавления */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    color: 'text.secondary',
                                }}
                            >
                                УЧАСТНИКИ · {group.memberCount}
                            </Typography>
                            {isAdmin && (
                                <Button
                                    size="small"
                                    startIcon={<PersonAddIcon />}
                                    disabled={busy}
                                    onClick={() => {
                                        setAddingMembers((prev) => !prev);
                                        setSelectedIds([]);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        color: '#9c27b0',
                                    }}
                                >
                                    {addingMembers ? 'Скрыть' : 'Добавить'}
                                </Button>
                            )}
                        </Box>

                        {/* Панель добавления участников */}
                        {addingMembers && isAdmin && (
                            <Box
                                sx={{
                                    bgcolor: 'white',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    p: 1,
                                    mb: 1.5,
                                }}
                            >
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Найти пользователя"
                                    value={memberQuery}
                                    onChange={(event) => setMemberQuery(event.target.value)}
                                    disabled={busy}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {directoryLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                ) : candidates.length === 0 ? (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 1 }}
                                    >
                                        Некого добавить — все пользователи уже в группе
                                    </Typography>
                                ) : (
                                    <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {candidates.map((user) => (
                                            <ListItem key={user.id} disablePadding>
                                                <FormControlLabel
                                                    sx={{ m: 0, width: '100%', px: 1 }}
                                                    control={(
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedIds.includes(user.id)}
                                                            onChange={() => handleToggleMember(user.id)}
                                                            disabled={busy}
                                                            inputProps={{ 'aria-label': `Выбрать ${user.fullName}` }}
                                                        />
                                                    )}
                                                    label={(
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {user.fullName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {user.email}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="contained"
                                    disabled={busy || selectedIds.length === 0}
                                    onClick={() => void handleAddMembers()}
                                    sx={{
                                        mt: 1,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        bgcolor: '#9c27b0',
                                        '&:hover': { bgcolor: '#7b1fa2' },
                                        boxShadow: 'none',
                                        borderRadius: 2,
                                    }}
                                >
                                    Добавить выбранных ({selectedIds.length})
                                </Button>
                            </Box>
                        )}

                        {/* Состав группы */}
                        <List
                            dense
                            sx={{
                                bgcolor: 'white',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                py: 0,
                            }}
                        >
                            {members.map((member) => {
                                const isSelf = currentUserId !== null
                                    && member.userId === currentUserId;
                                const actions = getMemberActions(member, {
                                    currentUserId,
                                    currentUserRole: role,
                                    adminCount,
                                });

                                return (
                                    <ListItem
                                        key={member.userId}
                                        sx={{ py: 0.75 }}
                                        secondaryAction={actions.length > 0 ? (
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                aria-label={`Действия с участником ${member.fullName}`}
                                                disabled={busy}
                                                onClick={(event) => {
                                                    setMenuAnchor(event.currentTarget);
                                                    setMenuUserId(member.userId);
                                                }}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined}
                                    >
                                        <ListItemAvatar>
                                            <Badge
                                                overlap="circular"
                                                variant="dot"
                                                invisible={!member.isOnline}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        bgcolor: '#44b700',
                                                        color: '#44b700',
                                                        boxShadow: '0 0 0 2px #fff',
                                                    },
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 13,
                                                        bgcolor: isGroupAdmin(member.role)
                                                            ? '#9c27b0'
                                                            : '#b0bec5',
                                                    }}
                                                >
                                                    {getInitials(member.fullName)}
                                                </Avatar>
                                            </Badge>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={(
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    alignItems="center"
                                                    sx={{ minWidth: 0 }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        noWrap
                                                        sx={{ maxWidth: 150 }}
                                                    >
                                                        {isSelf ? `${member.fullName} (вы)` : member.fullName}
                                                    </Typography>
                                                    {isGroupAdmin(member.role) && (
                                                        <Chip
                                                            label="админ"
                                                            size="small"
                                                            sx={{
                                                                height: 16,
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                bgcolor: '#f3e5f5',
                                                                color: '#7b1fa2',
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            )}
                                            secondary={member.email !== '' ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    component="span"
                                                >
                                                    {member.email}
                                                </Typography>
                                            ) : undefined}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </>
                )}
            </DialogContent>

            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={closeMenu}>
                {menuActions.map((action) => (
                    <MenuItem
                        key={action}
                        onClick={() => void handleMemberAction(action)}
                        sx={MEMBER_ACTIONS[action].danger ? { color: 'error.main' } : undefined}
                    >
                        <ListItemIcon sx={MEMBER_ACTIONS[action].danger ? { color: 'error.main' } : undefined}>
                            {MEMBER_ACTIONS[action].icon}
                        </ListItemIcon>
                        <ListItemText primary={MEMBER_ACTIONS[action].label} />
                    </MenuItem>
                ))}
            </Menu>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: 'white',
                    borderTop: '1px solid rgba(0,0,0,0.05)',
                    gap: 1,
                    justifyContent: 'space-between',
                }}
            >
                {group && isAdmin && (
                    confirmDelete ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                size="small"
                                color="error"
                                variant="contained"
                                disabled={busy}
                                onClick={() => void handleDelete()}
                                sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
                            >
                                Да, удалить
                            </Button>
                            <Button
                                size="small"
                                disabled={busy}
                                onClick={() => setConfirmDelete(false)}
                                sx={{ textTransform: 'none' }}
                            >
                                Отмена
                            </Button>
                        </Stack>
                    ) : (
                        <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            disabled={busy}
                            onClick={() => {
                                setConfirmLeave(false);
                                setConfirmDelete(true);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Удалить группу
                        </Button>
                    )
                )}

                {group && (
                    confirmLeave ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                size="small"
                                color="error"
                                variant="contained"
                                disabled={busy}
                                onClick={() => void handleLeave()}
                                sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
                            >
                                Выйти
                            </Button>
                            <Button
                                size="small"
                                disabled={busy}
                                onClick={() => setConfirmLeave(false)}
                                sx={{ textTransform: 'none' }}
                            >
                                Отмена
                            </Button>
                        </Stack>
                    ) : (
                        <Button
                            size="small"
                            startIcon={<LogoutIcon />}
                            disabled={busy}
                            onClick={() => {
                                setConfirmDelete(false);
                                setConfirmLeave(true);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                        >
                            Выйти из группы
                        </Button>
                    )
                )}

                <Button
                    onClick={onClose}
                    disabled={busy}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
};
